import type { CarSpec, StyleAction } from './types.js';

export interface VehicleSignature {
  id: string;
  name: string;
  description: string;
  /** Consumption only; the boost's top-speed and acceleration remain shared. */
  nitroDrain: number;
  comboWindow: number;
  driftReward: number;
  cornerReward: number;
  draftReward: number;
  routeReward: number;
  airReward: number;
  combatReward: number;
}

const BALANCED_SIGNATURE: VehicleSignature = {
  id: 'balanced-flow',
  name: 'FLOW STATE',
  description: 'A longer style-chain window rewards smooth, connected driving.',
  nitroDrain: 1,
  comboWindow: 1.18,
  driftReward: 1,
  cornerReward: 1,
  draftReward: 1,
  routeReward: 1,
  airReward: 1,
  combatReward: 1,
};

const SIGNATURES: Readonly<Record<string, VehicleSignature>> = {
  'vortex-r': BALANCED_SIGNATURE,
  'apex-gt': {
    ...BALANCED_SIGNATURE,
    id: 'long-burn',
    name: 'LONG BURN',
    description: 'High-speed aero stretches every tank of nitro further.',
    nitroDrain: 0.82,
    comboWindow: 1,
  },
  'tempest-x': {
    ...BALANCED_SIGNATURE,
    id: 'apex-hunter',
    name: 'APEX HUNTER',
    description: 'Perfect corners bank more style and exit nitro.',
    comboWindow: 1,
    cornerReward: 1.28,
  },
  'nightfall-rs': {
    ...BALANCED_SIGNATURE,
    id: 'precision-chain',
    name: 'PRECISION CHAIN',
    description: 'Clean corners and drafting build a stronger technical chain.',
    comboWindow: 1.08,
    cornerReward: 1.16,
    draftReward: 1.14,
  },
  'titan-xr': {
    ...BALANCED_SIGNATURE,
    id: 'smoke-show',
    name: 'SMOKE SHOW',
    description: 'Caught drifts and sustained slides pay a larger style dividend.',
    comboWindow: 1,
    driftReward: 1.3,
  },
  'mammoth-6x6': {
    ...BALANCED_SIGNATURE,
    id: 'trailblazer',
    name: 'TRAILBLAZER',
    description: 'Rough and technical alternate routes pay extra.',
    comboWindow: 1,
    routeReward: 1.3,
  },
  'juggernaut-rig': {
    ...BALANCED_SIGNATURE,
    id: 'road-presence',
    name: 'ROAD PRESENCE',
    description: 'Drafting, clean passes and takedowns reward the heavyweight line.',
    comboWindow: 1,
    draftReward: 1.2,
    combatReward: 1.18,
  },
  'ironclad-t9': {
    ...BALANCED_SIGNATURE,
    id: 'overmatch',
    name: 'OVERMATCH',
    description: 'Takedowns and cannon plays build extra style and nitro.',
    comboWindow: 1,
    combatReward: 1.38,
  },
  'volt-r9': {
    ...BALANCED_SIGNATURE,
    id: 'air-attack',
    name: 'AIR ATTACK',
    description: 'Aerial control turns clean landings into larger rewards.',
    nitroDrain: 0.92,
    comboWindow: 1,
    airReward: 1.38,
  },
  'nomad-xr': {
    ...BALANCED_SIGNATURE,
    id: 'wild-line',
    name: 'WILD LINE',
    description: 'Jumps and gravel shortcuts refill the attack faster.',
    comboWindow: 1,
    routeReward: 1.32,
    airReward: 1.18,
  },
  'wiener-wagon': {
    ...BALANCED_SIGNATURE,
    id: 'smoke-show',
    name: 'RELISH THE SLIDE',
    description: 'Long, saucy drifts pay a larger style dividend.',
    comboWindow: 1,
    driftReward: 1.26,
  },
  quackster: {
    ...BALANCED_SIGNATURE,
    id: 'apex-hunter',
    name: 'DUCK AND WEAVE',
    description: 'Perfect corners bank more style and exit nitro.',
    comboWindow: 1,
    cornerReward: 1.24,
  },
  'couch-rocket': {
    ...BALANCED_SIGNATURE,
    id: 'balanced-flow',
    name: 'COUCH POTATO FLOW',
    description: 'A longer style-chain window rewards relaxed, connected driving.',
  },
  'bigfoot-bubble': {
    ...BALANCED_SIGNATURE,
    id: 'wild-line',
    name: 'BIG AIR BUBBLE',
    description: 'Jumps and rough shortcuts refill the attack faster.',
    comboWindow: 1,
    routeReward: 1.24,
    airReward: 1.26,
  },
};

export function vehicleSignatureFor(car: Pick<CarSpec, 'id'>): VehicleSignature {
  return SIGNATURES[car.id] ?? BALANCED_SIGNATURE;
}

export function signatureRewardMultiplier(
  signature: VehicleSignature,
  action: Exclude<StyleAction, '' | 'CHAIN LOST'>,
): number {
  switch (action) {
    case 'DRIFT':
    case 'DRIFT BOOST':
      return signature.driftReward;
    case 'GOOD CORNER':
    case 'PERFECT CORNER':
      return signature.cornerReward;
    case 'DRAFT':
    case 'SLINGSHOT':
    case 'CLEAN PASS':
    case 'REVENGE':
    case 'OVERTAKE':
      return signature.draftReward;
    case 'RISK LINE':
    case 'SHORTCUT':
    case 'LOOP MASTER':
    case 'CROWD LINE':
      return signature.routeReward;
    case 'AIR':
      return signature.airReward;
    case 'TAKEDOWN':
      return signature.combatReward;
    case 'NEAR MISS':
    case 'GOOD LAUNCH':
    case 'PERFECT LAUNCH':
    case 'WHEELSPIN':
    case 'BOOST ZONE':
    case 'PIT CREW':
    case 'LIVE CONTRACT':
    case 'COMEBACK':
    case 'HEAT UP':
    case 'BANKED':
    case 'BOSS BLOCK':
    case 'BOSS ATTACK':
    case 'BOSS DRIFT':
    case 'BOSS SHORTCUT':
    case 'SIGNATURE':
    case 'REWIND':
      return 1;
  }
}
